import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_WIDGET_PAGE_ID } from '@/lib/db';
import type { WidgetPage } from '@/types';

const STORAGE_KEY = 'widget-current-page-id';

export interface WidgetPageStateContextValue {
  widgetPages: WidgetPage[];
  currentPageId: string;
  currentPage: WidgetPage | undefined;
  setCurrentPageId: (pageId: string) => void;
}

export const WidgetPageStateContext = createContext<WidgetPageStateContextValue>({
  widgetPages: [],
  currentPageId: DEFAULT_WIDGET_PAGE_ID,
  currentPage: undefined,
  setCurrentPageId: () => {},
});

export function useWidgetPageStateProvider(
  widgetPages: WidgetPage[],
  onPageChange?: () => void,
): WidgetPageStateContextValue {
  const optimisticPageIdRef = useRef<string | null>(null);
  const fallbackPageId = widgetPages[0]?.id ?? DEFAULT_WIDGET_PAGE_ID;
  const [currentPageId, setCurrentPageIdState] = useState(() => (
    localStorage.getItem(STORAGE_KEY) || DEFAULT_WIDGET_PAGE_ID
  ));

  const hasPage = useCallback(
    (pageId: string) => widgetPages.some((page) => page.id === pageId),
    [widgetPages],
  );

  const resolvePageId = useCallback((pageId: string) => {
    const candidatePageId = pageId || DEFAULT_WIDGET_PAGE_ID;
    if (widgetPages.length === 0) return candidatePageId;
    if (hasPage(candidatePageId) || optimisticPageIdRef.current === candidatePageId) {
      return candidatePageId;
    }
    return fallbackPageId;
  }, [fallbackPageId, hasPage, widgetPages.length]);

  const setCurrentPageId = useCallback((pageId: string) => {
    if (pageId && widgetPages.length > 0 && !hasPage(pageId)) {
      optimisticPageIdRef.current = pageId;
    }
    const nextPageId = resolvePageId(pageId);
    if (currentPageId !== nextPageId) onPageChange?.();
    setCurrentPageIdState(nextPageId);
    localStorage.setItem(STORAGE_KEY, nextPageId);
  }, [currentPageId, hasPage, onPageChange, resolvePageId, widgetPages.length]);

  useEffect(() => {
    if (widgetPages.length === 0) return;

    const optimisticPageId = optimisticPageIdRef.current;
    if (optimisticPageId) {
      if (hasPage(optimisticPageId)) {
        optimisticPageIdRef.current = null;
      } else if (currentPageId === optimisticPageId) {
        return;
      }
    }

    const nextPageId = resolvePageId(currentPageId);
    if (nextPageId !== currentPageId) {
      onPageChange?.();
      setCurrentPageIdState(nextPageId);
      localStorage.setItem(STORAGE_KEY, nextPageId);
    }
  }, [currentPageId, hasPage, onPageChange, resolvePageId, widgetPages.length]);

  const currentPage = useMemo(
    () => widgetPages.find((page) => page.id === currentPageId),
    [currentPageId, widgetPages],
  );

  return {
    widgetPages,
    currentPageId,
    currentPage,
    setCurrentPageId,
  };
}

export function useWidgetPageState() {
  return useContext(WidgetPageStateContext);
}
